import { useMemo } from "react";
import { Text, mergeStyleSets } from "@fluentui/react";
import { IGameEntry, ITeam, ITeamScore, IPhase } from "./LiveScoreboard";

interface ITeamStanding {
    teamId: string;
    wins: number;
    losses: number;
    totalPoints: number;
    totalBonusPoints: number;
    totalBonusesHeard: number;
    gamesPlayed: number;
}

interface IStandingsSectionProps {
    games: Record<string, IGameEntry>;
    phases: IPhase[];
    teamDictionary: Record<string, ITeam>;
}

const styles = mergeStyleSets({
    phaseSection: {
        marginTop: 24,
    },
    phaseHeader: {
        fontSize: 22,
        fontWeight: 600,
        marginTop: 25,
        marginBottom: 4,
        paddingBottom: 6,
        borderBottom: "2px solid #0078d4",
    },
    bracketsGrid: {
        display: "flex",
        flexWrap: "wrap" as const,
        gap: 12,
        marginTop: 8,
    },
    bracketSection: {
        flex: "1 1 0",
        minWidth: 200,
        maxWidth: "25%",
        marginBottom: 8,
        "@media (max-width: 900px)": {
            maxWidth: "100%",
            flex: "1 1 100%",
        },
    },
    bracketHeader: {
        fontSize: 14,
        fontWeight: 600,
        marginBottom: 4,
    },
    table: {
        width: "100%",
        borderCollapse: "collapse" as const,
        border: "1px solid #e1e1e1",
    },
    headerRow: {
        backgroundColor: "#f3f2f1",
    },
    headerCell: {
        textAlign: "left" as const,
        padding: "4px 6px",
        borderBottom: "2px solid #edebe9",
        fontSize: 12,
        fontWeight: 600,
    },
    headerCellCenter: {
        textAlign: "center" as const,
        padding: "4px 6px",
        borderBottom: "2px solid #edebe9",
        fontSize: 12,
        fontWeight: 600,
    },
    headerCellRight: {
        textAlign: "right" as const,
        padding: "4px 6px",
        borderBottom: "2px solid #edebe9",
        fontSize: 12,
        fontWeight: 600,
    },
    cell: {
        padding: "3px 6px",
        borderBottom: "1px solid #edebe9",
        fontSize: 12,
    },
    cellCenter: {
        padding: "3px 6px",
        borderBottom: "1px solid #edebe9",
        fontSize: 12,
        textAlign: "center" as const,
    },
    cellRight: {
        padding: "3px 6px",
        borderBottom: "1px solid #edebe9",
        fontSize: 12,
        textAlign: "right" as const,
    },
    evenRow: {
        backgroundColor: "#faf9f8",
    },
});

function addGameToStandings(
    standings: Record<string, Record<string, Record<string, ITeamStanding>>>,
    phaseName: string,
    bracketName: string,
    game: IGameEntry
): void {
    if (!standings[phaseName]) {
        standings[phaseName] = {};
    }

    if (!standings[phaseName][bracketName]) {
        standings[phaseName][bracketName] = {};
    }

    const teamIds = Object.keys(game.teams);

    if (teamIds.length !== 2) {
        return;
    }

    const [teamAId, teamBId] = teamIds;
    const teamAScore = game.teams[teamAId];
    const teamBScore = game.teams[teamBId];
    const aWins = teamAScore.score > teamBScore.score;

    const addTeamResult = (teamId: string, score: ITeamScore, won: boolean) => {
        if (!standings[phaseName][bracketName][teamId]) {
            standings[phaseName][bracketName][teamId] = {
                teamId,
                wins: 0,
                losses: 0,
                totalPoints: 0,
                totalBonusPoints: 0,
                totalBonusesHeard: 0,
                gamesPlayed: 0,
            };
        }
        const s = standings[phaseName][bracketName][teamId];
        s.gamesPlayed++;
        s.totalPoints += score.score;
        s.totalBonusPoints += score.bonusPoints;

        const tossupPoints = score.score - score.bonusPoints;
        s.totalBonusesHeard += score.bonusesHeard ?? Math.max(Math.round(tossupPoints / 10), 0);

        if (won) s.wins++;
        else s.losses++;
    };

    addTeamResult(teamAId, teamAScore, aWins);
    addTeamResult(teamBId, teamBScore, !aWins);
}

function computeAllStandings(
    games: Record<string, IGameEntry>,
    phases: IPhase[]
): Record<string, Record<string, Record<string, ITeamStanding>>> {
    const result: Record<string, Record<string, Record<string, ITeamStanding>>> = {};
    const sortedPhases = [...phases].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    for (const game of Object.values(games)) {
        if (!game.isFinished) {
            continue;
        }

        const teamIds = Object.keys(game.teams);
        if (teamIds.length !== 2) {
            continue;
        }

        // just skip tiebreaker games for standings
        if (game.tiebreaker) {
            continue;
        }

        // Add to the game's own phase/bracket
        addGameToStandings(result, game.phase, game.bracket, game);

        // Carryover: if both teams are in the same bracket in a later phase,
        // count this game for that later bracket as well
        const gamePhase = sortedPhases.find((p) => p.name === game.phase);
        const gamePhaseOrder = gamePhase?.order ?? 0;

        for (const phase of sortedPhases) {
            if ((phase.order ?? 0) <= gamePhaseOrder
                || phase.noCarryovers) {
                continue;
            }

            for (const bracket of phase.brackets) {
                if (bracket.teamIds.includes(teamIds[0]) && bracket.teamIds.includes(teamIds[1])) {
                    addGameToStandings(result, phase.name, bracket.name, game);
                }
            }
        }
    }

    return result;
}

export default function StandingsSection({ games, phases, teamDictionary }: IStandingsSectionProps) {
    const standings = useMemo(() => computeAllStandings(games, phases), [games, phases]);

    // Sort phases descending by order (later phases first)
    const sortedPhases = useMemo(
        () => [...phases].sort((a, b) => (b.order ?? 0) - (a.order ?? 0)),
        [phases]
    );

    if (sortedPhases.length === 0) return null;

    return (
        <>
            {sortedPhases.map((phase) => {
                const phaseStandings = standings[phase.name];
                if (!phaseStandings) return null;

                const hasData = phase.brackets.some((b) => {
                    const bs = phaseStandings[b.name];
                    return bs && Object.values(bs).some((s) => s.gamesPlayed > 0);
                });

                if (!hasData) return null;

                return (
                    <div key={phase.name} className={styles.phaseSection}>
                        <Text id={`standings-${phase.name.toLowerCase().replace(/\s+/g, "-")}`} className={styles.phaseHeader} block>
                            {phase.name} Standings
                        </Text>
                        <div className={styles.bracketsGrid}>
                            {phase.brackets.map((bracket) => {
                                const bs = phaseStandings[bracket.name];
                                if (!bs) return null;

                                const sorted = Object.values(bs)
                                    .filter((s) => s.gamesPlayed > 0)
                                    .sort((a, b) => {
                                        if (b.wins !== a.wins) return b.wins - a.wins;
                                        const aPPG = a.totalPoints / a.gamesPlayed;
                                        const bPPG = b.totalPoints / b.gamesPlayed;
                                        return bPPG - aPPG;
                                    });

                                if (sorted.length === 0) return null;

                                return (
                                    <div key={bracket.name} className={styles.bracketSection}>
                                        <Text className={styles.bracketHeader} block>
                                            {bracket.name}
                                        </Text>
                                        <table className={styles.table}>
                                            <thead>
                                                <tr className={styles.headerRow}>
                                                    <th className={styles.headerCell}>Team</th>
                                                    <th className={styles.headerCellCenter}>Record</th>
                                                    <th className={styles.headerCellRight}>PPG</th>
                                                    <th className={styles.headerCellRight}>PPB</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sorted.map((s, i) => {
                                                    const ppg = (s.totalPoints / s.gamesPlayed).toFixed(1);
                                                    const ppb =
                                                        s.totalBonusesHeard > 0
                                                            ? (s.totalBonusPoints / s.totalBonusesHeard).toFixed(2)
                                                            : "0.00";
                                                    return (
                                                        <tr key={s.teamId} className={i % 2 === 1 ? styles.evenRow : undefined}>
                                                            <td className={styles.cell}>
                                                                {teamDictionary[s.teamId]?.name ?? s.teamId}
                                                            </td>
                                                            <td className={styles.cellCenter}>
                                                                {s.wins} - {s.losses}
                                                            </td>
                                                            <td className={styles.cellRight}>{ppg}</td>
                                                            <td className={styles.cellRight}>{ppb}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </>
    );
}
